import type { AnalyticsSite } from '../../api/types';

/**
 * The Install screen's rules, kept out of the components so they can be pinned.
 *
 * Every check here mirrors what the SERVICE does with a value, because the
 * service never refuses a bad entry — it drops it, and the screen used to say
 * "Saved." either way:
 *
 * - `normalizeOrigin` (services/analytics/sites.js) turns anything that is not
 *   a full http(s) address into nothing. An operator who typed `example.com`
 *   saved an EMPTY list, which means "accept any origin": the opposite of what
 *   they asked for.
 * - Origins and excluded addresses are compared by exact string
 *   (`callerAllowed`, controller/pxCont.js), so a wildcard or a range is a
 *   rule that silently never matches.
 * - An excluded page must look like a stored path (services/analytics/paths.js
 *   `isExcluded`), so one without its leading slash never matches anything.
 * - Retention narrows only: `updateSite` clamps it to 90 days, so a longer
 *   choice was saved as 90 while the screen kept showing the longer one.
 */

export type InstallSection = 'install' | 'collection' | 'funnel' | 'privacy' | 'data' | 'location';

const INSTALL_SECTIONS: readonly InstallSection[] = ['install', 'collection', 'funnel', 'privacy', 'data', 'location'];

/** Install and settings, opened on one install from outside the screen. */
export interface ManageLink {
  siteId: number;
  section: InstallSection;
}

/**
 * `/dashboard/analytics?manage=install&site=12` — the link the dispatch
 * dashboard's **Manage in CRM** opens (through `/sso`) for the install its
 * Website analytics switch is pointed at. Null for anything else, which leaves
 * the screen to do exactly what it does without one: a link from outside is a
 * suggestion, and a malformed one must not become an error page.
 *
 * Whether the install exists — and whether this reader may see the section —
 * is the screen's to decide once it has the list; this only reads the address.
 */
export const manageLinkOf = (search: string): ManageLink | null => {
  const params = new URLSearchParams(search);
  const section = params.get('manage');
  const siteId = Number(params.get('site'));
  if (!INSTALL_SECTIONS.includes(section as InstallSection)) return null;
  if (!Number.isSafeInteger(siteId) || siteId <= 0) return null;
  return { siteId, section: section as InstallSection };
};

/** The service's `NAME_MAX` (services/analytics/installs.js), which cuts a longer name without saying so. */
export const INSTALL_NAME_MAX = 120;

/** A textarea of one-per-line values, which is how people actually paste them. */
export const linesToList = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

export const listToLines = (list: readonly string[] | null | undefined): string =>
  (list ?? []).join('\n');

export const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export interface LineProblem {
  line: string;
  message: string;
}

/** The service's `normalizeOrigin`, line for line: a full http(s) address, reduced to its origin. */
export const originOf = (raw: string): string | null => {
  if (!raw.trim()) return null;
  try {
    const url = new URL(raw.trim());
    return /^https?:$/.test(url.protocol) ? url.origin : null;
  } catch {
    return null;
  }
};

export const originProblems = (lines: readonly string[]): LineProblem[] =>
  lines.flatMap((line) => {
    if (line.includes('*')) {
      return [{ line, message: 'Wildcards are not matched — list each address on its own line.' }];
    }
    if (originOf(line)) return [];
    const guess = originOf(`https://${line}`);
    return [
      {
        line,
        message: guess ? `Needs the https:// part — write it as ${guess}` : 'This is not a web address.',
      },
    ];
  });

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
// Loose on purpose: an address the proxy reports is the authority, and a strict
// IPv6 grammar here would refuse a valid compressed form sooner or later.
const IPV6 = /^[0-9a-f:.]+$/i;

export const ipProblems = (lines: readonly string[]): LineProblem[] =>
  lines.flatMap((line) => {
    if (line.includes('/') || line.includes('-') || line.includes('*')) {
      return [{ line, message: 'Ranges are not matched — list each address on its own line.' }];
    }
    const v4 = IPV4.exec(line);
    if (v4) {
      return v4.slice(1).every((part) => Number(part) <= 255)
        ? []
        : [{ line, message: 'This is not an IP address.' }];
    }
    if (line.includes(':') && IPV6.test(line)) return [];
    return [{ line, message: 'This is not an IP address.' }];
  });

export const pathProblems = (lines: readonly string[]): LineProblem[] =>
  lines.flatMap((line) => {
    if (line.startsWith('/')) return [];
    // A whole address pasted in: what is stored is its path, so offer that.
    const asUrl = originOf(line) ? new URL(line.trim()).pathname : null;
    const fix = asUrl && asUrl !== '/' ? asUrl : `/${line.replace(/^\/*/, '')}`;
    return [{ line, message: `Pages are matched by their path — write it as ${fix}` }];
  });

/** The service's `VISITOR_KEY_RE` — checked here so a typo is not a confirm dialog and then a 404. */
const VISITOR_KEY = /^av_[A-Za-z0-9_-]{16,48}$/;

export const visitorKeyProblem = (raw: string): string | null =>
  VISITOR_KEY.test(raw.trim()) ? null : 'Visitor ids start with av_ followed by 16 or more letters and digits.';

/* ---- the booking funnel ------------------------------------------------- */

export interface FunnelStep {
  key: string;
  label: string;
  match?: { type: 'path' | 'event'; value: string };
}

/**
 * A key no other step holds.
 *
 * Keys are what `leadStep` and the funnel report point at, so they must stay
 * unique and must not follow the label around. `step_<count + 1>` alone
 * collided: remove the first of three generated steps and the next one added
 * was a second `step_3`.
 */
export const nextStepKey = (steps: readonly FunnelStep[]): string => {
  const taken = new Set(steps.map((step) => step.key));
  let n = steps.length + 1;
  while (taken.has(`step_${n}`)) n += 1;
  return `step_${n}`;
};

/**
 * The same steps, with a blank or repeated key replaced.
 *
 * The old key rule shipped, so an install can already hold two `step_3`s; read
 * as they are, the second would be a lead-step option nobody can tell apart
 * from the first. The first holder keeps its key, so a lead step that points at
 * it still does.
 */
export const withUniqueKeys = (steps: readonly FunnelStep[]): FunnelStep[] => {
  const out: FunnelStep[] = [];
  for (const step of steps) {
    const taken = out.some((kept) => kept.key === step.key);
    out.push(step.key && !taken ? step : { ...step, key: nextStepKey([...out, ...steps]) });
  }
  return out;
};

/**
 * What is wrong with one step, or null.
 *
 * A PAGE step is a prefix match (`p.path LIKE '<value>%'` in the funnel read),
 * so an empty one matches every visit and reads as a funnel where nobody ever
 * drops out at that step.
 */
export const stepProblem = (step: FunnelStep): string | null => {
  if (!step.label.trim()) return 'Give this step a name.';
  const type = step.match?.type ?? 'path';
  const value = (step.match?.value ?? '').trim();
  if (!value) return type === 'event' ? 'Name the event your site reports.' : 'Give the start of the page address.';
  if (type === 'path' && !value.startsWith('/')) return `Page addresses start with a slash — /${value}`;
  return null;
};

export const moveStep = <T>(list: readonly T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length || from === to) return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/* ---- retention ---------------------------------------------------------- */

/** The ceiling `updateSite` enforces — a promise made in the privacy notice, not a preference. */
export const RETENTION_CEILING_DAYS = 90;

/** The choices on offer, plus whatever the install already holds if it is not one of them. */
export const retentionChoices = (current: number | null | undefined): number[] => {
  const base = [30, 60, RETENTION_CEILING_DAYS];
  const held = Math.max(1, Math.min(RETENTION_CEILING_DAYS, Number(current) || RETENTION_CEILING_DAYS));
  return base.includes(held) ? base : [...base, held].sort((a, b) => a - b);
};

/* ---- is it working ------------------------------------------------------ */

/**
 * The one state the Install section leads with. Collecting switched off beats
 * every health answer: an install that is off reads as quiet, and "check the
 * tag is still on your pages" is the wrong advice for a switch on this screen.
 */
export type InstallState = 'off' | 'waiting' | 'quiet' | 'ok';

export const installState = (site: Pick<AnalyticsSite, 'enabled' | 'health'>): InstallState =>
  !site.enabled
    ? 'off'
    : site.health === 'never_reported'
      ? 'waiting'
      : site.health === 'silent'
        ? 'quiet'
        : 'ok';

/**
 * The OTHER switched-on app installs that claim the same app as this one, by
 * `appConfig`'s rule (services/siteIntegrations.js): an app asks for the
 * install named with its bundle id, else the ONE named with none — and two of
 * either is no answer. So a second app install switched on beside the first
 * silently switches the app off for both, and the Install section is the one
 * place that can say so. Whether THIS install is on is the caller's question:
 * the same rivals are a warning before it is switched on and a fault after.
 */
export const appRivals = (
  site: Pick<AnalyticsSite, 'id' | 'platform' | 'bundleId'>,
  sites: readonly AnalyticsSite[],
): AnalyticsSite[] =>
  site.platform !== 'app'
    ? []
    : sites.filter(
        (other) =>
          other.id !== site.id &&
          other.platform === 'app' &&
          other.enabled &&
          (other.bundleId || null) === (site.bundleId || null),
      );

/* ---- what is left to do ------------------------------------------------- */

export interface SetupItem {
  key: string;
  label: string;
  detail: string;
  done: boolean;
  section: InstallSection;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The settings worth making after the paste, each with what it currently is.
 *
 * Only items whose state can actually be read are here. The confirmation-page
 * line matters more than any of them, but nothing the console can see says
 * whether a site sends it — a tick there would be invented, so it lives in the
 * numbered steps as "strongly recommended" instead.
 *
 * An app has no counterpart to Allowed sites on the list. Its bundle identifier
 * used to be one, but the passenger app now finds its install BY that id, so it
 * is not a lock worth nudging toward: an app install with none is the normal
 * answer, and naming one where the iOS and Android ids differ switches one of
 * the two apps off.
 */
export const setupChecklist = (site: AnalyticsSite, geoReady: boolean | null): SetupItem[] => {
  const app = site.platform === 'app';
  const origins = site.allowedOrigins ?? [];
  const ips = site.excludeIps ?? [];
  const steps = site.funnel ?? [];
  return [
    {
      key: 'reporting',
      label: app ? 'The app is sending data' : 'The tag is on your site',
      detail:
        site.health === 'never_reported'
          ? 'Nothing has arrived yet'
          : site.health === 'silent'
            ? 'Installed, but quiet for 24 hours'
            : 'Data is arriving',
      done: site.health !== 'never_reported',
      section: 'install',
    },
    ...(app
      ? []
      : [
          {
            key: 'origins',
            label: 'Only your own sites can report',
            detail: origins.length ? `${plural(origins.length, 'address', 'addresses')} allowed` : 'Any site is accepted',
            done: origins.length > 0,
            section: 'collection' as const,
          },
        ]),
    {
      key: 'team',
      label: 'Your team’s visits are left out',
      detail: ips.length ? `${plural(ips.length, 'office address', 'office addresses')} ignored` : 'Your own browsing counts as traffic',
      done: ips.length > 0,
      section: 'collection',
    },
    {
      key: 'funnel',
      label: 'The reservation funnel is mapped',
      detail: steps.length >= 2 ? plural(steps.length, 'step', 'steps') : steps.length === 1 ? 'Only one step so far' : 'Not set up',
      done: steps.length >= 2,
      section: 'funnel',
    },
    {
      key: 'location',
      label: 'Town-level locations',
      detail:
        geoReady === null ? 'Checking…' : geoReady ? 'Town and region are available' : 'Country only — needs a MaxMind key',
      done: geoReady === true,
      section: 'location',
    },
  ];
};
